export type HistoricalRateCause = {
  cause: string;
  /** true = regulator-approved rate case or plan; false = fuel/market-driven */
  planned: boolean;
  sourceUrl?: string;
};

// Cause attribution for PAST years' rate changes, keyed state → year. Rows
// without an entry render as a neutral "Actual" EIA line in RateChangeLedger.
//
// Researched 2026-08-20 (web sources, per-entry reasoning in PR #1764) — 31
// entries, 10 states. Sparse on purpose: a year is included ONLY with a solid
// source; never invent an entry. Same data-owner + update-cadence requirement
// as knownRateChanges.ts. Known judgment calls: CA 2022–23 mix approved
// wildfire capital with gas passthrough (classed planned); IL 2007 freeze
// expiry was scheduled but market-priced (classed planned); TX 2011/2012/2016
// cite weaker secondary sources — upgrade to EIA Today in Energy URLs when
// convenient.
export const historicalRateCauses: Record<string, Record<number, HistoricalRateCause>> = {
  AZ: {
    2024: {
      cause:
        'ACC-approved APS rate case (~8% for a typical bill, effective March 2024) for grid maintenance and reliability investments',
      planned: true,
      sourceUrl: 'https://azcc.gov/news/home/2024/02/23/acc-votes-on-arizona-public-service-company-rate-case',
    },
  },
  CA: {
    2020: {
      cause:
        'Utility wildfire-mitigation and grid-hardening spending began outpacing inflation across PG&E, SCE, and SDG&E rates',
      planned: true,
      sourceUrl:
        'https://www.utilitydive.com/news/californias-dilemma-how-to-control-skyrocketing-electric-rates-while-buil/597767/',
    },
    2021: {
      cause:
        'CPUC-approved rate cases funding wildfire mitigation and grid safety (SCE 2021 GRC; similar PG&E increases)',
      planned: true,
      sourceUrl: 'https://www.cpuc.ca.gov/news-and-updates/all-news/cpuc-issues-decision-in-sce-2021-rate-case',
    },
    2022: {
      cause:
        'Wildfire-mitigation and power-line investment costs plus higher winter natural-gas prices across the major utilities',
      planned: true,
      sourceUrl: 'https://www.publicadvocates.cpuc.ca.gov/press-room/reports-and-analyses/2022-electric-rates-report',
    },
    2023: {
      cause:
        'CPUC-authorized wildfire-prevention and insurance cost recovery, compounded by a winter natural-gas price spike',
      planned: true,
      sourceUrl:
        'https://calmatters.org/environment/2024/12/pge-utilities-wildfire-prevention-customer-bills-california/',
    },
    2024: {
      cause:
        'PG&E’s CPUC-approved general rate case (~13% in January 2024) funding undergrounding and wildfire-safety work',
      planned: true,
      sourceUrl: 'https://www.cbsnews.com/sanfrancisco/news/pg-e-rate-hike-cpuc-utility-bill/',
    },
    2025: {
      cause:
        'Smaller increases as temporary wildfire-safety and storm-response cost recoveries rolled off utility bills',
      planned: true,
      sourceUrl:
        'https://www.pge.com/en/newsroom/currents/energy-savings/about-energy-rates-whats-ahead-and-how-to-lower-energy-costs.html',
    },
  },
  FL: {
    2022: {
      cause:
        'FPL’s PSC-approved four-year rate settlement took effect, plus higher natural-gas fuel costs passed through to bills',
      planned: true,
      sourceUrl:
        'https://newsroom.fpl.com/2021-10-26-PSC-unanimously-approves-FPLs-four-year-rate-settlement-agreement,-keeping-bills-low-and-accelerating-U-S-s-largest-solar-buildout',
    },
    2023: {
      cause:
        'Recovery of 2022 natural-gas fuel under-collections plus Hurricane Ian and Nicole storm-restoration surcharges',
      planned: false,
      sourceUrl:
        'https://newsroom.fpl.com/2023-01-23-FPL-proposes-plan-to-recover-costs-of-increased-fuel-prices-and-hurricane-responses',
    },
    2024: {
      cause:
        'Hurricane-restoration surcharges expired and falling natural-gas prices cut fuel charges on utility bills',
      planned: false,
      sourceUrl:
        'https://newsroom.nexteraenergy.com/2024-04-02-FPL-gets-green-light-to-reduce-rates-for-the-second-consecutive-month?l=12',
    },
  },
  GA: {
    2023: {
      cause:
        'Georgia Power recovery of $2.1B in higher natural-gas fuel costs, plus the first Vogtle nuclear unit entering rates',
      planned: false,
      sourceUrl: 'https://www.gpb.org/news/2023/04/17/georgia-power-psc-staff-agree-on-fuel-costs-recovery',
    },
    2024: {
      cause: 'Georgia PSC put Plant Vogtle nuclear-expansion construction costs into Georgia Power rates',
      planned: true,
      sourceUrl:
        'https://www.ajc.com/news/psc-raises-georgia-power-rates-passing-most-plant-vogtle-expansion-costs-on-to-customers/6BAIOWM7J5BVHFZ2UN27KYXENA/',
    },
  },
  IL: {
    2007: {
      cause:
        'The state’s 10-year rate freeze expired and utilities bought power at auction at market prices (ComEd ~25%, Ameren territories up to 55%)',
      planned: true,
      sourceUrl: 'https://news.illinois.edu/what-you-need-to-know-about-the-spike-in-illinois-electric-rates-2/',
    },
    2022: {
      cause:
        'Downstate capacity shortage in the MISO auction and higher power-supply prices spiked Ameren supply rates (~40% in June)',
      planned: false,
      sourceUrl: 'https://senatorrezin.com/2022/06/10/ameren-electric-rates-increased/',
    },
    2025: {
      cause:
        'Record PJM capacity-auction prices raised ComEd supply rates starting June 2025 (~$10.60/month for a typical bill)',
      planned: false,
      sourceUrl:
        'https://www.citizensutilityboard.org/blog/2025/05/09/cub-qa-capacity-price-spike-means-comed-supply-price-will-shoot-up-june-2025/',
    },
  },
  MA: {
    2022: {
      cause:
        'Natural-gas-driven basic-service supply increases, including a ~64% National Grid jump for winter 2022–23',
      planned: false,
      sourceUrl:
        'https://www.boston.com/news/local-news/2022/11/01/massachusetts-energy-rate-increases-eversource-national-grid/',
    },
    2023: {
      cause:
        'Global natural-gas price spike after the Ukraine invasion drove winter supply rates up 40%+ at Eversource and National Grid',
      planned: false,
      sourceUrl:
        'https://www.wbur.org/news/2022/11/18/eversource-western-massachusetts-electricity-rates-hike-2022-2023-winter',
    },
  },
  NC: {
    2023: {
      cause: 'Duke Energy Progress’s NCUC-approved multi-year rate plan funding grid-resilience upgrades took effect',
      planned: true,
      sourceUrl:
        'https://news.duke-energy.com/releases/duke-energy-progress-receives-approval-for-new-rates-in-north-carolina-implements-new-programs-to-help-customers',
    },
    2024: {
      cause:
        'Duke Energy Carolinas’s NCUC-approved multi-year rate plan (8.3% year one) funding $7.7B in grid upgrades',
      planned: true,
      sourceUrl:
        'https://news.duke-energy.com/releases/duke-energy-carolinas-receives-approval-for-new-rates-in-north-carolina-implements-new-programs-to-help-customers',
    },
  },
  NJ: {
    2025: {
      cause:
        'Record PJM capacity-auction prices flowed into the annual BGS supply auction, raising bills 17–20% from June 2025',
      planned: false,
      sourceUrl: 'https://www.utilitydive.com/news/new-jersey-electric-bills-pjm-bpu-pseg-auction/740053/',
    },
  },
  NY: {
    2021: {
      cause: 'Higher wholesale electricity and natural-gas prices raised the supply portion of bills across the state',
      planned: false,
      sourceUrl:
        'https://www.naturalgasintel.com/new-england-new-york-utilities-warn-natural-gas-bills-could-soar-up-to-38-from-2021-winter-rate-spike-part-1/',
    },
    2022: {
      cause:
        'Natural-gas market spike (pandemic recovery and the Ukraine war) passed through to supply rates — ~22% higher NYC winter bills',
      planned: false,
      sourceUrl:
        'https://ny1.com/nyc/all-boroughs/news/2022/09/10/con-edison-warns-consumers-of-expected-higher-winter-prices',
    },
    2023: {
      cause:
        'PSC-approved three-year Con Edison rate plan raised electric delivery rates (~9% bill increase in year one)',
      planned: true,
      sourceUrl: 'https://nysfocus.com/2023/08/07/energy-bill-rate-hikes-psc-coned',
    },
    2024: {
      cause:
        'Second-year delivery increases under Con Edison’s PSC-approved 2023–2025 rate plan, plus other utilities’ rate plans',
      planned: true,
      sourceUrl: 'https://nysfocus.com/2023/08/07/energy-bill-rate-hikes-psc-coned',
    },
    2025: {
      cause:
        'Final-year Con Edison rate-plan increase plus newly approved National Grid upstate delivery rates (from September 2025)',
      planned: true,
      sourceUrl: 'https://www.wwnytv.com/2025/08/14/national-grid-rate-hike-approved-bills-increase-next-month/',
    },
  },
  TX: {
    2008: {
      cause:
        'Natural-gas prices hit record summer highs (Henry Hub ~$13/MMBtu), passed through by competitive retail providers',
      planned: false,
      sourceUrl: 'https://www.ferc.gov/sites/default/files/2020-05/som-rpt-2008.pdf',
    },
    2011: {
      cause:
        'Booming shale-gas production pushed natural-gas and ERCOT wholesale power prices down, lowering retail offers',
      planned: false,
      sourceUrl: 'https://www.electricchoice.com/historical-electricity-pricing/',
    },
    2012: {
      cause:
        'Natural-gas prices at 10-year lows and a record-warm winter kept cutting wholesale and retail power prices',
      planned: false,
      sourceUrl: 'https://www.electricchoice.com/historical-electricity-pricing/',
    },
    2016: {
      cause:
        'Cheap natural gas lowered generation costs — part of the first nationwide residential price decline since 2002',
      planned: false,
      sourceUrl:
        'https://www.utilitydive.com/news/eia-us-residential-electricity-prices-set-to-decline-for-first-time-since/427778/',
    },
    2021: {
      cause:
        'Winter Storm Uri sent gas and wholesale power prices to record levels, repricing retail plans and adding storm-cost recovery',
      planned: false,
      sourceUrl: 'https://comptroller.texas.gov/economy/fiscal-notes/archive/2021/oct/winter-storm-reform.php',
    },
    2022: {
      cause:
        'Natural-gas price spike (June rates more than double a year earlier) plus Uri storm-cost securitization fees on bills',
      planned: false,
      sourceUrl: 'https://www.dallasfed.org/research/swe/2022/swe2204/swe2204e',
    },
  },
};
